class PlpBagFinderPopup extends HTMLElement {
  connectedCallback() {
    this.launcher = this.querySelector('[data-plp-bag-finder-launcher]');
    this.dismissButton = this.querySelector('[data-plp-bag-finder-dismiss]');
    this.dismissKey = this.dataset.dismissKey || 'sg:plp-bag-finder:dismissed';
    this.isDesignMode = this.dataset.designMode === 'true';
    this.dialog = this.querySelector('.plp-bag-finder-popup__dialog');
    this.slideshow = this.querySelector('slideshow-component');
    this.pagination = this.querySelector('[data-plp-bag-finder-pagination]');
    this.paginationDots = this.querySelector('[data-plp-bag-finder-pagination-dots]');
    this.paginationPrevious = this.querySelector('[data-plp-bag-finder-pagination-previous]');
    this.paginationNext = this.querySelector('[data-plp-bag-finder-pagination-next]');
    this.activeFilter = this.getCurrentFilter();
    this.pageStarts = [];

    if (this.dismissButton) {
      this.dismissButton.addEventListener('click', this.handleDismiss);
    }

    if (this.launcher && this.dismissButton) {
      this.restoreDismissState();
    }

    this.setupPagination();
  }

  disconnectedCallback() {
    if (this.dismissButton) {
      this.dismissButton.removeEventListener('click', this.handleDismiss);
    }

    if (this.paginationPrevious) {
      this.paginationPrevious.removeEventListener('click', this.handlePaginationPrevious);
    }

    if (this.paginationNext) {
      this.paginationNext.removeEventListener('click', this.handlePaginationNext);
    }

    if (this.slideshow) {
      this.slideshow.removeEventListener('slideshow:select', this.handleSlideshowSelect);
    }

    this.removeEventListener('click', this.handleFilterClick);

    if (this.dialogObserver) {
      this.dialogObserver.disconnect();
    }

    if (this.slideObserver) {
      this.slideObserver.disconnect();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('resize', this.schedulePaginationSync);
    window.removeEventListener('resize', this.scheduleSlideshowRefresh);

    if (this.paginationFrame) {
      cancelAnimationFrame(this.paginationFrame);
    }

    if (this.refreshFrame) {
      cancelAnimationFrame(this.refreshFrame);
    }

    if (this.deferredRefreshTimeout) {
      clearTimeout(this.deferredRefreshTimeout);
    }
  }

  handleDismiss = () => {
    this.hideLauncher();

    if (this.isDesignMode) return;

    try {
      window.sessionStorage.setItem(this.dismissKey, '1');
    } catch (_) {
      // Ignore storage failures and fall back to the current page state only.
    }
  };

  restoreDismissState() {
    if (this.isDesignMode) return;

    try {
      if (window.sessionStorage.getItem(this.dismissKey) === '1') {
        this.hideLauncher();
      }
    } catch (_) {
      // Ignore storage failures and keep the launcher visible.
    }
  }

  hideLauncher() {
    if (!this.launcher) return;

    this.launcher.hidden = true;
  }

  async setupPagination() {
    if (!this.pagination || !this.paginationDots || !this.slideshow) return;

    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected) return;

    this.paginationPrevious?.addEventListener('click', this.handlePaginationPrevious);
    this.paginationNext?.addEventListener('click', this.handlePaginationNext);
    this.slideshow.addEventListener('slideshow:select', this.handleSlideshowSelect);
    this.addEventListener('click', this.handleFilterClick);

    if (this.dialog) {
      this.dialogObserver = new MutationObserver(this.handleDialogMutation);
      this.dialogObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    }

    this.slideObserver = new MutationObserver(this.schedulePaginationSync);
    this.slideObserver.observe(this.slideshow, {
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    const scroller = this.slideshow.refs?.scroller || this.slideshow.querySelector('slideshow-slides');
    if (scroller) {
      this.resizeObserver = new ResizeObserver(this.schedulePaginationSync);
      this.resizeObserver.observe(scroller);
    }

    window.addEventListener('resize', this.schedulePaginationSync);
    window.addEventListener('resize', this.scheduleSlideshowRefresh);
    this.schedulePaginationSync();
    this.scheduleSlideshowRefresh();
  }

  handleDialogMutation = () => {
    if (this.dialog?.hasAttribute('open')) {
      this.scheduleSlideshowRefresh();
    }

    this.schedulePaginationSync();
  };

  handleSlideshowSelect = () => {
    this.updatePaginationState();
  };

  handleFilterClick = (event) => {
    const filterButton = event.target instanceof Element ? event.target.closest('.bag-finder__filter-btn') : null;
    if (!filterButton) return;

    this.activeFilter = filterButton.dataset.filter || 'all';
    this.scheduleSlideshowRefresh({ resetToFirst: true });
    this.schedulePaginationSync();
  };

  handlePaginationPrevious = (event) => {
    event.preventDefault();

    if (!this.pageStarts.length) return;

    const currentPage = this.getCurrentPageIndex();
    const previousPage = Math.max(0, currentPage - 1);
    this.selectPage(previousPage);
  };

  handlePaginationNext = (event) => {
    event.preventDefault();

    if (!this.pageStarts.length) return;

    const currentPage = this.getCurrentPageIndex();
    const nextPage = Math.min(this.pageStarts.length - 1, currentPage + 1);
    this.selectPage(nextPage);
  };

  schedulePaginationSync = () => {
    if (this.paginationFrame) {
      cancelAnimationFrame(this.paginationFrame);
    }

    this.paginationFrame = requestAnimationFrame(() => {
      this.syncPagination();
    });
  };

  scheduleSlideshowRefresh = ({ resetToFirst = false } = {}) => {
    if (this.refreshFrame) {
      cancelAnimationFrame(this.refreshFrame);
    }

    if (this.deferredRefreshTimeout) {
      clearTimeout(this.deferredRefreshTimeout);
    }

    this.refreshFrame = requestAnimationFrame(() => {
      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshSlideshowLayout({ resetToFirst });
      });
    });

    this.deferredRefreshTimeout = window.setTimeout(() => {
      this.refreshSlideshowLayout({ resetToFirst });
    }, 180);
  };

  refreshSlideshowLayout({ resetToFirst = false } = {}) {
    if (!this.slideshow || !this.dialog?.hasAttribute('open')) return;

    const activeSlides = this.getActiveSlides();
    if (!activeSlides.length) {
      this.syncCenteredLayout(activeSlides);
      return;
    }

    this.syncCenteredLayout(activeSlides);

    const currentIndex = Number.isFinite(this.slideshow.current) ? this.slideshow.current : 0;
    const targetIndex = resetToFirst ? 0 : Math.min(Math.max(currentIndex, 0), activeSlides.length - 1);

    this.forceSelectSlide(targetIndex);
    this.syncPagination();
  }

  forceSelectSlide(targetIndex) {
    if (!this.slideshow) return;

    const activeSlides = this.getActiveSlides();
    if (!activeSlides.length) return;

    const safeIndex = Math.min(Math.max(targetIndex, 0), activeSlides.length - 1);
    const shouldForce = (this.slideshow.visibleSlides?.length || 0) === 0 || this.slideshow.current === safeIndex;

    if (shouldForce) {
      this.slideshow.current = -1;
    }

    this.slideshow.select(safeIndex, undefined, { animate: false });
  }

  syncPagination() {
    if (!this.pagination || !this.paginationDots || !this.slideshow) return;

    const activeSlides = this.getActiveSlides();
    this.syncVisibleSlideDividers(activeSlides);
    this.syncCenteredLayout(activeSlides);

    if (!activeSlides.length) {
      this.pagination.hidden = true;
      return;
    }

    const visibleSlidesCount = this.getVisibleSlidesCount(activeSlides);
    this.pageStarts = this.getPageStarts(activeSlides.length, visibleSlidesCount);

    if (this.pageStarts.length <= 1) {
      this.pagination.hidden = true;
      this.renderPaginationDots();
      return;
    }

    this.pagination.hidden = false;
    this.renderPaginationDots();
    this.updatePaginationState();
  }

  syncVisibleSlideDividers(activeSlides = this.getActiveSlides()) {
    if (!this.slideshow) return;

    const allSlides = this.slideshow.querySelectorAll('slideshow-slide');
    allSlides.forEach((slide) => {
      slide.removeAttribute('data-plp-divider-before');
    });

    activeSlides.forEach((slide, index) => {
      if (index > 0) {
        slide.setAttribute('data-plp-divider-before', '');
      }
    });
  }

  syncCenteredLayout(activeSlides = this.getActiveSlides()) {
    if (!this.slideshow) return;

    const scroller = this.slideshow.refs?.scroller || this.slideshow.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const contentWidth = this.getActiveSlidesContentWidth(activeSlides, scroller);
    const centerOffset = Math.max(0, (scroller.clientWidth - contentWidth) / 2);
    const shouldCenterSlides = this.activeFilter !== 'all' && activeSlides.length > 0 && centerOffset > 1;

    if (shouldCenterSlides) {
      scroller.setAttribute('data-plp-filter-centered', '');
      scroller.style.setProperty('--plp-filter-center-offset', `${centerOffset}px`);
      scroller.scrollLeft = 0;
      return;
    }

    scroller.removeAttribute('data-plp-filter-centered');
    scroller.style.removeProperty('--plp-filter-center-offset');
  }

  getCurrentFilter() {
    const activeButton = this.querySelector('.bag-finder__filter-btn[aria-pressed="true"]');
    return activeButton?.dataset.filter || 'all';
  }

  getActiveSlides() {
    if (!this.slideshow) return [];

    return Array.from(this.slideshow.querySelectorAll('slideshow-slide')).filter((slide) => !slide.hasAttribute('hidden'));
  }

  getVisibleSlidesCount(activeSlides) {
    if (!this.slideshow) return 1;

    const visibleSlidesCount = this.dialog?.hasAttribute('open') ? this.slideshow.visibleSlides?.length || 0 : 0;
    if (visibleSlidesCount > 0) {
      return visibleSlidesCount;
    }

    const scroller = this.slideshow.refs?.scroller || this.slideshow.querySelector('slideshow-slides');
    const firstSlide = activeSlides[0];
    if (!(scroller instanceof HTMLElement) || !(firstSlide instanceof HTMLElement)) {
      return 1;
    }

    const scrollerStyles = window.getComputedStyle(scroller);
    const gap = parseFloat(scrollerStyles.columnGap || scrollerStyles.gap || '0') || 0;
    const slideWidth = firstSlide.getBoundingClientRect().width;
    if (slideWidth <= 0) {
      return 1;
    }

    return Math.max(1, Math.floor((scroller.clientWidth + gap) / (slideWidth + gap)));
  }

  getActiveSlidesContentWidth(activeSlides, scroller) {
    if (!(scroller instanceof HTMLElement) || !activeSlides.length) return 0;

    const scrollerStyles = window.getComputedStyle(scroller);
    const gap = parseFloat(scrollerStyles.columnGap || scrollerStyles.gap || '0') || 0;
    const slidesWidth = activeSlides.reduce((total, slide) => total + slide.getBoundingClientRect().width, 0);

    return slidesWidth + gap * Math.max(0, activeSlides.length - 1);
  }

  getPageStarts(slideCount, visibleSlidesCount) {
    const step = Math.max(1, visibleSlidesCount);
    const starts = [];

    for (let index = 0; index < slideCount; index += step) {
      starts.push(index);
    }

    return starts;
  }

  renderPaginationDots() {
    if (!this.paginationDots) return;

    this.paginationDots.innerHTML = '';

    this.pageStarts.forEach((_, pageIndex) => {
      const listItem = document.createElement('li');
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'plp-bag-finder-popup__pagination-dot button-unstyled';
      button.setAttribute('aria-label', `Go to bag page ${pageIndex + 1}`);
      button.dataset.pageIndex = String(pageIndex);
      button.addEventListener('click', this.handlePaginationDotClick);

      listItem.append(button);
      this.paginationDots.append(listItem);
    });
  }

  handlePaginationDotClick = (event) => {
    event.preventDefault();

    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const pageIndex = Number(button.dataset.pageIndex);
    if (Number.isNaN(pageIndex)) return;

    this.selectPage(pageIndex);
  };

  selectPage(pageIndex) {
    if (!this.slideshow) return;

    const slideIndex = this.pageStarts[pageIndex];
    if (slideIndex == null) return;

    this.slideshow.select(slideIndex);
  }

  getCurrentPageIndex() {
    if (!this.pageStarts.length || !this.slideshow) return 0;

    const currentSlideIndex = this.slideshow.current || 0;
    let currentPageIndex = 0;

    this.pageStarts.forEach((pageStart, index) => {
      if (currentSlideIndex >= pageStart) {
        currentPageIndex = index;
      }
    });

    return currentPageIndex;
  }

  updatePaginationState() {
    if (!this.paginationDots || !this.pageStarts.length) return;

    const currentPageIndex = this.getCurrentPageIndex();
    const dotButtons = this.paginationDots.querySelectorAll('.plp-bag-finder-popup__pagination-dot');

    dotButtons.forEach((button, index) => {
      button.setAttribute('aria-selected', String(index === currentPageIndex));
    });

    if (this.paginationPrevious) {
      this.paginationPrevious.disabled = currentPageIndex === 0;
    }

    if (this.paginationNext) {
      this.paginationNext.disabled = currentPageIndex === this.pageStarts.length - 1;
    }
  }
}

if (!customElements.get('plp-bag-finder-popup')) {
  customElements.define('plp-bag-finder-popup', PlpBagFinderPopup);
}
